import {
  RequirementStatus,
  SignatureRequest
} from "@wingspanhq/wingspan-api-sdk";

import { getRequirementDefinitionTableCellProps } from "../../../../../RequirementDefinitions/utils";
import { useDownloadVaultFile } from "../../../../hooks/useDownloadVaultFile";
import { selectorIsRequirementReadyToSignAsPayee } from "../../../../selectors/selectorIsRequirementReadyToSignAsPayee";
import { selectorIsRequirementReadyToSignAsPayer } from "../../../../selectors/selectorIsRequirementReadyToSignAsPayer";
import {
  CellsConfig,
  ContextItem,
  RequirementAction,
  RequirementTableCallback,
  RequirementTableItemData
} from "../../../../types";
import { getRequirementEngagementsTableCellProps } from "../../../../utils";
import { SignaturesFieldsWidget } from "../../components/SignaturesFieldsWidget";
import { useSignSignatureRequest } from "../../hooks/useSignSignatureRequest";
import { getSignaturePayerRequirementStatus } from "../helpers/getSignaturePayerRequirementStatus";
import { getSignatureRequestProgressSteps } from "../helpers/getSignatureRequestProgressSteps";
import { getSignatureRequirementName } from "../helpers/getSignatureRequirementName";
import { getHasPayerSigner } from "../helpers/getSignerByRole";
import { SIGNATURE_REQUIREMENT_EVENT } from "../utils";

import { payeeSignatureRenderCardDescription } from "./payeeSignatureRenderCardDescription";

export const usePayeeSignatureRequirementConfig = (
  callback: RequirementTableCallback
): CellsConfig<
  RequirementTableItemData,
  ContextItem,
  SignatureRequest | undefined
> => {
  const [signSignatureRequest] = useSignSignatureRequest();
  const downloadVaultFile = useDownloadVaultFile();

  // v3 keeps a single fully executed copy on the request — the payer's where a
  // payer signature exists, otherwise the payee's. v1 minted a separate member
  // and client file and each side downloaded its own.
  const handleRequirementDownload = async (
    requirement: RequirementTableItemData,
    completedFileId: string
  ) => {
    if (await downloadVaultFile(completedFileId)) {
      callback(SIGNATURE_REQUIREMENT_EVENT.DownloadAsPayer, requirement);
    }
  };

  const handleRequirementCountersign = async (
    requirement: RequirementTableItemData,
    signatureRequest: SignatureRequest
  ) => {
    await signSignatureRequest(signatureRequest, {
      onSuccess() {
        callback(SIGNATURE_REQUIREMENT_EVENT.SignAsPayer, requirement);
      }
    });
  };

  const getDownloadAction = (
    requirement: RequirementTableItemData,
    context: ContextItem,
    signatureRequest: SignatureRequest | undefined
  ) => {
    const isDocumentReadyToDownload =
      requirement.dataSourceStatus === RequirementStatus.COMPLETED;
    const completedFileId = signatureRequest?.completedFileId;

    if (completedFileId && isDocumentReadyToDownload) {
      return async () => {
        await handleRequirementDownload(requirement, completedFileId);
      };
    }

    return undefined;
  };

  const getCounterSignAction = (
    requirement: RequirementTableItemData,
    context: ContextItem,
    signatureRequest: SignatureRequest | undefined
  ) => {
    if (
      selectorIsRequirementReadyToSignAsPayer(requirement, signatureRequest)
    ) {
      return async () => {
        await handleRequirementCountersign(requirement, signatureRequest!);
      };
    }

    return undefined;
  };

  return {
    isActionRequired: (requirement, context, signatureRequest) => {
      const counterSignAction = getCounterSignAction(
        requirement,
        context,
        signatureRequest
      );

      return !!counterSignAction;
    },
    requirementStatusAlert: (requirement, context, signatureRequest) => {
      if (
        selectorIsRequirementReadyToSignAsPayer(requirement, signatureRequest)
      ) {
        return {
          theme: "warning",
          title: "Your signature required",
          children: "Your signature is required to complete the requirement.",
          icon: "time"
        };
      } else if (
        selectorIsRequirementReadyToSignAsPayee(requirement, signatureRequest)
      ) {
        return {
          theme: "info",
          title: "Awaiting contractor",
          children: "Awaiting contractor to complete the requirement.",
          icon: "time"
        };
      }
    },
    requirementDetailsProgressWidget: (
      requirement,
      context,
      signatureRequest
    ) => {
      return signatureRequest
        ? getSignatureRequestProgressSteps(
            signatureRequest,
            getHasPayerSigner(signatureRequest)
          )
        : undefined;
    },
    requirementDetailsWidgets: (requirement, context, signatureRequest) => {
      if (signatureRequest?.responseItems?.length) {
        return <SignaturesFieldsWidget signatureRequest={signatureRequest} />;
      }

      return null;
    },
    requirementName: getSignatureRequirementName,
    requirementDetails: (requirement, context, signatureRequest) => {
      const downloadAction = getDownloadAction(
        requirement,
        context,
        signatureRequest
      );

      const signAction = getCounterSignAction(
        requirement,
        context,
        signatureRequest
      );

      return {
        header: {
          label: {
            text: getSignatureRequirementName(
              requirement,
              context,
              signatureRequest
            )
          }
        },
        children: payeeSignatureRenderCardDescription(
          requirement,
          signatureRequest
        ),
        data: [],
        actions:
          downloadAction || signAction
            ? {
                alignment: "fill",
                buttons: [
                  {
                    label: "Download file",
                    onAsyncClick: downloadAction,
                    kind: "Secondary",
                    visible: !!downloadAction
                  },
                  {
                    label: "Start",
                    onAsyncClick: signAction,
                    kind: "Primary",
                    visible: !!signAction
                  }
                ]
              }
            : undefined
      };
    },
    toolbarActions: (requirement, context, signatureRequest) => {
      const actions: RequirementAction[] = [];

      const downloadAction = getDownloadAction(
        requirement,
        context,
        signatureRequest
      );

      const counterSignAction = getCounterSignAction(
        requirement,
        context,
        signatureRequest
      );

      if (counterSignAction) {
        actions.push({
          label: "Sign document",
          onAsyncClick: counterSignAction,
          buttonKind: "Primary",
          icon: "edit-line",
          buttonIcon: null,
          mainAction: true
        });
      }

      if (downloadAction) {
        actions.push({
          onAsyncClick: downloadAction,
          label: "Download file",
          buttonKind: counterSignAction ? undefined : "Secondary",
          icon: "download",
          buttonIcon: null
        });
      }

      return actions;
    },
    nameCell: (requirement, context, signatureRequest) => {
      return {
        text: getSignatureRequirementName(
          requirement,
          context,
          signatureRequest
        )
      };
    },
    typeCell: requirement =>
      getRequirementDefinitionTableCellProps({
        type: requirement.type
      }),
    statusCell: (requirement, context, signatureRequest) => {
      return getSignaturePayerRequirementStatus(
        requirement,
        context,
        signatureRequest
      );
    },
    engagementsCell: getRequirementEngagementsTableCellProps
  };
};
